// Users, roles and invitations at a location (identity.* operations, cloud-only): the reads behind the Users and User
// Roles pages and the commands they send. Role and invite ids are minted here (UUIDv7), so a retried attempt replays.
import type { z } from 'zod';
import type { InviteView, MemberView, PermissionKey, RoleView } from '@radish/contracts';
import { useCommand, useLocationQuery } from '../../data/api.tsx';
import { uuidv7 } from '../../lib/ids.ts';

export type Member = z.output<typeof MemberView>;
export type Invite = z.output<typeof InviteView>;
export type Role = z.output<typeof RoleView>;
/** `email` mails the link; `link` hands it to the admin once, to pass on ("Copy Invite Link"). */
type InviteDelivery = 'email' | 'link';

const MEMBERS = 'identity.members.list';
const INVITES = 'identity.invites.list';
const ROLES = 'identity.roles.list';

export function useMembers(locationId: string) {
  return useLocationQuery(locationId, MEMBERS, { locationId }, (c) => c.queries.identity.members.list({ locationId: locationId as never }));
}

export function useInvites(locationId: string) {
  return useLocationQuery(locationId, INVITES, { locationId }, (c) => c.queries.identity.invites.list({ locationId: locationId as never }));
}

export function useRoles(locationId: string) {
  return useLocationQuery(locationId, ROLES, { locationId }, (c) => c.queries.identity.roles.list({ locationId: locationId as never }));
}

export function useInviteUser(locationId: string) {
  return useCommand(
    locationId,
    (c, input: { readonly email: string; readonly roleId: string; readonly delivery: InviteDelivery }, call) =>
      c.commands.identity.inviteUser({ inviteId: uuidv7() as never, locationId: locationId as never, email: input.email, roleId: input.roleId as never, delivery: input.delivery }, call),
    [INVITES],
  );
}

export function useResendInvite(locationId: string) {
  return useCommand(
    locationId,
    (c, input: { readonly inviteId: string; readonly delivery: InviteDelivery }, call) =>
      c.commands.identity.resendInvite({ inviteId: input.inviteId as never, locationId: locationId as never, delivery: input.delivery }, call),
    [INVITES],
  );
}

export function useCancelInvite(locationId: string) {
  return useCommand(locationId, (c, inviteId: string, call) => c.commands.identity.cancelInvite({ inviteId: inviteId as never, locationId: locationId as never }, call), [INVITES]);
}

export function useChangeMemberRole(locationId: string) {
  return useCommand(
    locationId,
    (c, input: { readonly userId: string; readonly roleId: string }, call) =>
      c.commands.identity.changeMemberRole({ userId: input.userId as never, locationId: locationId as never, roleId: input.roleId as never }, call),
    [MEMBERS, ROLES],
  );
}

export function useRemoveMember(locationId: string) {
  return useCommand(locationId, (c, userId: string, call) => c.commands.identity.removeMember({ userId: userId as never, locationId: locationId as never }, call), [MEMBERS, ROLES]);
}

interface RoleDraft {
  readonly roleId: string | null;
  readonly name: string;
  readonly description: string;
  readonly permissions: readonly PermissionKey[];
}

export function useUpsertRole(locationId: string) {
  return useCommand(
    locationId,
    (c, draft: RoleDraft, call) => c.commands.identity.upsertRole({
      roleId: (draft.roleId ?? uuidv7()) as never, locationId: locationId as never, name: draft.name.trim(),
      description: draft.description.trim() === '' ? null : draft.description.trim(), permissions: [...draft.permissions],
    }, call),
    [ROLES, MEMBERS],
  );
}

export function useDeleteRole(locationId: string) {
  return useCommand(locationId, (c, roleId: string, call) => c.commands.identity.deleteRole({ roleId: roleId as never, locationId: locationId as never }, call), [ROLES, MEMBERS, INVITES]);
}

/**
 * Legacy's moment().fromNow() (`Utils.dateAgo`), with moment's default thresholds and rounding: "a few seconds ago",
 * "a minute ago", "3 minutes ago", "an hour ago", "a day ago", "6 days ago", "a month ago", "a year ago", "in 6 days".
 */
export function relativeTime(iso: string, nowMs: number): string {
  const diff = Date.parse(iso) - nowMs;
  const ms = Math.abs(diff);
  const seconds = Math.round(ms / 1000);
  const minutes = Math.round(ms / 60_000);
  const hours = Math.round(ms / 3_600_000);
  const days = Math.round(ms / 86_400_000);
  // moment's daysToMonths: 146097 days are 4800 months.
  const monthsExact = (ms / 86_400_000) * 4800 / 146_097;
  const months = Math.round(monthsExact);
  const years = Math.round(monthsExact / 12);
  const phrase =
    seconds < 45 ? 'a few seconds'
      : minutes <= 1 ? 'a minute'
      : minutes < 45 ? `${minutes} minutes`
      : hours <= 1 ? 'an hour'
      : hours < 22 ? `${hours} hours`
      : days <= 1 ? 'a day'
      : days < 26 ? `${days} days`
      : months <= 1 ? 'a month'
      : months < 11 ? `${months} months`
      : years <= 1 ? 'a year'
      : `${years} years`;
  return diff > 0 ? `in ${phrase}` : `${phrase} ago`;
}
